import { useAppSelector } from "@app/hooks";
import PDFFooter from "@components/pdf-partials/PDFFooter";
import PDFPropertyHeader from "@components/pdf-partials/PDFPropertyHeader";
import TransformedImage from "@components/TransformedImage";
import { hasAddressLine2AsCustom } from "@shared/utils";
import { transformImageUrl } from "@shared/utils/imageUrlTransforms";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import { getPropertyDisplayIndex } from "@survey-preview/utils/propertyIndexUtils";
import type { DetailedSurvey } from "@survey-types/survey.core.types";
import type { Property } from "@survey-types/survey.property.types";
import {
  selectCurrentSurvey,
  selectMediaFiles,
  selectSurveyConfig,
  selectVisibleProperties,
} from "@survey/surveySelectors";
import classNames from "classnames";

interface PropertyImagesPage {
  title: string;
  subtitle: string;
  order: number;
  images: string[];
}

interface PropertyImagePreviewProps {
  mode?: string;
  propertyFilter?: string; // Optional filter for specific property ID
}

// Modern utility functions
const getArrayChunks = <T,>(array: T[], chunkSize: number) =>
  Array.from({ length: Math.ceil(array.length / chunkSize) }, (_, i) =>
    array.slice(i * chunkSize, i * chunkSize + chunkSize),
  );

const getFullAddressStr = (
  address1?: string,
  city?: string,
  state?: string,
  postalCode?: string,
) => [address1, city, state, postalCode].filter(Boolean).join(", ");

const PropertyImagePreview = (props: PropertyImagePreviewProps) => {
  const { mode, propertyFilter } = props;

  // Use store-first architecture with selectors
  const survey = useAppSelector(selectCurrentSurvey);
  const surveyConfig = useAppSelector(selectSurveyConfig);
  const allVisibleProperties = useAppSelector(selectVisibleProperties);
  const mediaFiles = useAppSelector(selectMediaFiles);

  // Filter properties if propertyFilter is provided
  const visibleProperties = propertyFilter
    ? allVisibleProperties.filter((prop) => prop.propertyId === propertyFilter)
    : allVisibleProperties;

  // Compute property pages directly (RTK Query already handles memoization)
  const getPropertiesPreview = (): PropertyImagesPage[] => {
    if (!mediaFiles || !visibleProperties.length) {
      return [];
    }

    const allPropertyPages: PropertyImagesPage[] = [];

    visibleProperties.forEach((property, propertyIndex) => {
      const propertyMedia = mediaFiles[property.propertyId];

      if (!propertyMedia?.images?.length) return;

      // Get all images except the first one (which is used as feature image)
      const additionalImages = propertyMedia.images
        .slice(1) // Skip first image (feature image)
        .filter((img) => !img.isHidden && img.cloudfrontUrl)
        .map((img) =>
          transformImageUrl(img.cloudfrontUrl!, img.origin || "", "standard"),
        );

      if (additionalImages.length === 0) return;

      // Calculate images per page based on paper size
      const isLandscape = surveyConfig.pdfPaperSize === "Landscape";
      const imagesPerPage = isLandscape ? 4 : 6;

      // Split images into chunks
      const imageChunks = getArrayChunks(additionalImages, imagesPerPage);

      // Create pages for this property
      const propertyPages: PropertyImagesPage[] = imageChunks.map((images) => ({
        title: property.name || "Property",
        subtitle: getFullAddressStr(
          property.address1,
          property.city,
          property.stateProvince,
          property.postalCode,
        ),
        order: propertyIndex + 1,
        images,
      }));

      allPropertyPages.push(...propertyPages);
    });

    return allPropertyPages;
  };

  const propertiesPreview = getPropertiesPreview();

  // Component to render image grid
  const PropertyImagesGrid = ({
    images,
    isLandscape,
  }: {
    images: string[];
    isLandscape: boolean;
  }) => {
    // Portrait: 2 cols × 3 rows = 6 images, Landscape: 2 cols × 2 rows = 4 images
    const gridClasses = classNames(
      "grid grid-cols-2 gap-y-2 gap-x-3 px-8 py-4",
      {
        "grid-rows-2": isLandscape,
        "grid-rows-3": !isLandscape,
      },
    );

    // Fixed dimensions: 284px height with 8px padding, width varies by orientation
    /* const imageWidth = isLandscape ? 465 : 375;
    const imageHeight = 276; */

    return (
      <div className={gridClasses}>
        {images.map((imageUrl, index) => (
          <TransformedImage
            key={index}
            imageUrl={imageUrl}
            alt={`Property image ${index + 1}`}
            className="overflow-hidden max-h-69 size-full"
            asBackground={false}
            showFallback={true}
          />
        ))}
      </div>
    );
  };

  // Individual property image page component
  interface PropertyImagePageProps {
    propertyImagesPage: PropertyImagesPage;
    pageIndex: number;
    property: NonNullable<Property>;
    survey: NonNullable<DetailedSurvey>;
    mode?: string;
  }

  const PropertyImagePage = ({
    propertyImagesPage,
    pageIndex,
    property,
    survey,
    mode,
  }: PropertyImagePageProps) => {
    const isLandscape = survey?.pdfPaperSize === "Landscape";
    const hasAddressLine2Custom = survey
      ? hasAddressLine2AsCustom(survey)
      : true;

    return (
      <SurveyPDFPage
        key={`property-images-${propertyImagesPage.order}-${pageIndex}`}
        mode={mode}
      >
        <div className="grid grid-rows-[auto_1fr_auto] h-full w-full bg-white">
          {/* Header */}
          <PDFPropertyHeader
            property={property}
            index={getPropertyDisplayIndex(property, survey)}
            showCurrentLocationIcon={survey?.showCurrentLocationPD}
            fieldRules={survey?.surveyPropertyFieldRule?.propertyFieldRuleList}
            hasAddressLine2Custom={hasAddressLine2Custom}
          />

          {/* Images Grid */}
          <PropertyImagesGrid
            images={propertyImagesPage.images}
            isLandscape={isLandscape}
          />

          {/* Footer */}
          <PDFFooter />
        </div>
      </SurveyPDFPage>
    );
  };

  // Early return after all hooks have been called
  if (!mediaFiles || propertiesPreview.length === 0) {
    return null;
  }

  return (
    <>
      {propertiesPreview.map((propertyImagesPage, pageIndex) => {
        const property = visibleProperties.find(
          (p) => p.name === propertyImagesPage.title,
        );

        if (!property || !survey) return null;

        return (
          <PropertyImagePage
            key={`property-images-${propertyImagesPage.order}-${pageIndex}`}
            propertyImagesPage={propertyImagesPage}
            pageIndex={pageIndex}
            property={property}
            survey={survey}
            mode={mode}
          />
        );
      })}
    </>
  );
};

export default PropertyImagePreview;
